<template>
  <InfoCard
    checkboxId="manufacturer-info-apply-variants"
    showApplyToVariants
    :applyToVariants="applyToVariants"
    :isSaving="combinedIsSaving"
    :showSuccessState="showSuccessState"
    :isSaved="combinedIsSaved"
    :saveButtonWidth="showSuccessState ? 'auto' : '183px'"
    saveButtonText="Değişiklikleri Kaydet"
    savedButtonText="Değişiklikler Kaydedildi"
    @saveChanges="handleSaveChanges"
    @update:applyToVariants="$emit('update:applyToVariants', $event)"
  >
  <div class="manufacturer-business-area">
    <div class="box border-line-right">
      <div class="title-area">
        <div class="title">Üretici Bilgileri</div>
        <div class="description">
          Ürünün üreticisine ait bilgiler ve adres içerir.
        </div>
      </div>
      <div
        v-for="(manufacturer, index) in manufacturers"
        :key="`manufacturer-${index}`"
        class="box-form-area"
        :class="{ disabled: isProductDisabled }"
      >
        <button
          v-if="manufacturers.length > 1 && index > 0"
          @click="!isProductDisabled && removeManufacturer(index)"
          class="remove-button"
          :class="{ disabled: isProductDisabled }"
          type="button"
        >
          <div class="remove-button-content">
            <Icons name="Delete" color="FF44EE" size="sm" />
            <span>Sil</span>
          </div>
        </button>
        <div class="input-wrapper">
          <Input
            label="Kayıtlı Ticari Ünvan"
            :id="`manufacturer-trade-name-${index}`"
            :isError="!!manufacturer.tradeNameError"
            v-model:value="manufacturer.tradeName"
            :disabled="isProductDisabled"
          />
          <p v-if="manufacturer.tradeNameError" class="error-text">
            {{ manufacturer.tradeNameError }}
          </p>
        </div>
        <div class="input-wrapper">
          <Input
            label="Marka"
            :id="`manufacturer-brand-${index}`"
            :isError="!!manufacturer.brandError"
            v-model:value="manufacturer.brand"
            :disabled="isProductDisabled"
          />
          <p v-if="manufacturer.brandError" class="error-text">
            {{ manufacturer.brandError }}
          </p>
        </div>
        <div class="textarea-wrapper">
          <Input
            type="textarea"
            label="İletişim adresi"
            :id="`manufacturer-address-${index}`"
            :class="{ error: manufacturer.addressError }"
            v-model:value="manufacturer.address"
            :isError="!!manufacturer.addressError"
            :disabled="isProductDisabled"
          />
          <p v-if="manufacturer.addressError" class="error-text">
            {{ manufacturer.addressError }}
          </p>
        </div>
        <div class="input-wrapper">
          <Input
            label="E-posta"
            :id="`manufacturer-email-${index}`"
            :isError="!!manufacturer.emailError"
            v-model:value="manufacturer.email"
            :disabled="isProductDisabled"
          />
          <p v-if="manufacturer.emailError" class="error-text">
            {{ manufacturer.emailError }}
          </p>
        </div>
        <DropdownSelectable
          :ref="(el) => setManufacturerOriginRef(el, index)"
          label="Menşei"
          :id="`manufacturer-origin-${index}`"
          :dropItems="manufacturerOrigins"
          :isRadioGroup="true"
          :isShowSearch="false"
          :errorText="manufacturer.originError"
          :disabled="isProductDisabled"
          @selectedRadioItem="(value) => handleOriginChange(value, index, 'manufacturer')"
          @changedDropdownState="(state) => handleDropdownStateChange(state, index, 'manufacturer')"
        />
      </div>
      <a
        v-if="manufacturers.length < 5"
        @click="!isProductDisabled && addManufacturer()"
        class="manufacturer-business-area-button"
        :class="{ disabled: isProductDisabled }"
      >
        <img src="@/assets/images/icons/plus-magenta.svg" />
        <p>Farklı Üretici Bilgisi Ekle</p>
      </a>
      <p v-else class="limit-text">
        En fazla 5 Üretici Bilgisi ekleyebilirsiniz.
      </p>
    </div>
    <div class="box">
      <div class="title-area">
        <div class="title">İthalatçı / İktisadi İşletmeci</div>
        <div class="description">
          Bir ürünün imalatçısı, yetkili temsilcisi, ithalatçısı, dağıtıcısı
          veya ürünün imalatına, piyasada bulundurulmasına veya hizmete
          sunulmasına ilişkin sorumluluğu olan diğer kişileri ifade eder.
        </div>
      </div>
      <div
        v-for="(importer, index) in importers"
        :key="`importer-${index}`"
        class="box-form-area"
        :class="{ disabled: isProductDisabled }"
      >
        <button
          v-if="importers.length > 1 && index > 0"
          @click="!isProductDisabled && removeImporter(index)"
          class="remove-button"
          :class="{ disabled: isProductDisabled }"
          type="button"
        >
          <div class="remove-button-content">
            <Icons name="Delete" color="FF44EE" size="sm" />
            <span>Sil</span>
          </div>
        </button>
        <div class="input-wrapper">
          <Input
            label="Kayıtlı Ticari Ünvan"
            :id="`importer-trade-name-${index}`"
            :isError="!!importer.tradeNameError"
            v-model:value="importer.tradeName"
            :disabled="isProductDisabled"
          />
          <p v-if="importer.tradeNameError" class="error-text">
            {{ importer.tradeNameError }}
          </p>
        </div>
        <div class="input-wrapper">
          <Input
            label="Marka"
            :id="`importer-brand-${index}`"
            :isError="!!importer.brandError"
            v-model:value="importer.brand"
            :disabled="isProductDisabled"
          />
          <p v-if="importer.brandError" class="error-text">
            {{ importer.brandError }}
          </p>
        </div>
        <div class="textarea-wrapper">
          <Input
            type="textarea"
            label="İletişim adresi"
            :id="`importer-address-${index}`"
            :class="{ error: importer.addressError }"
            v-model:value="importer.address"
            :isError="!!importer.addressError"
            :disabled="isProductDisabled"
          />
          <p v-if="importer.addressError" class="error-text">
            {{ importer.addressError }}
          </p>
        </div>
        <div class="input-wrapper">
          <Input
            label="E-posta"
            :id="`importer-email-${index}`"
            :isError="!!importer.emailError"
            v-model:value="importer.email"
            :disabled="isProductDisabled"
          />
          <p v-if="importer.emailError" class="error-text">
            {{ importer.emailError }}
          </p>
        </div>
        <DropdownSelectable
          :ref="(el) => setImporterOriginRef(el, index)"
          label="Menşei"
          :id="`importer-origin-${index}`"
          :dropItems="manufacturerOrigins"
          :isRadioGroup="true"
          :isShowSearch="false"
          :errorText="importer.originError"
          :disabled="isProductDisabled"
          @selectedRadioItem="(value) => handleOriginChange(value, index, 'importer')"
          @changedDropdownState="(state) => handleDropdownStateChange(state, index, 'importer')"
        />
      </div>
      <a
        v-if="importers.length < 5"
        @click="!isProductDisabled && addImporter()"
        class="manufacturer-business-area-button"
        :class="{ disabled: isProductDisabled }"
      >
        <img src="@/assets/images/icons/plus-magenta.svg" />
        <p>Farklı İthalatçı / İktisadi İşletmeci Ekle</p>
      </a>
      <p v-else class="limit-text">
        En fazla 5 İthalatçı / İktisadi İşletmeci ekleyebilirsiniz.
      </p>
    </div>
  </div>
  </InfoCard>
</template>

<script setup>
import { Input, DropdownSelectable, Icons } from "@n11/so-ui-components";
import { useStore } from "vuex";
import { ref, onMounted, watch, computed, nextTick } from "vue";
import InfoCard from "../InfoCard.vue";
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';

const store = useStore();

const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);

const hasManufacturerErrors = computed(() =>
  manufacturers.value.some((m) => {
    if (!hasAnyFieldFilled(m)) return false;
    return !!(m.tradeNameError || m.addressError || m.emailError || m.originError);
  })
);

const hasImporterErrors = computed(() =>
  importers.value.some((i) => {
    if (!hasAnyFieldFilled(i)) return false;
    return !!(i.tradeNameError || i.addressError || i.emailError || i.originError);
  })
);

const hasValidationError = computed(
  () => hasManufacturerErrors.value || hasImporterErrors.value
);

const props = defineProps({
  onSaveChanges: {
    type: Function,
    default: null,
  },
  safetyInfoData: {
    type: Object,
    default: null,
  },
  isSavingAllChanges: {
    type: Boolean,
    default: false,
  },
  applyToVariants: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:applyToVariants', 'hasChanges']);
const manufacturerOrigins = ref([]);

const manufacturers = ref([
  {
    tradeName: "",
    brand: "",
    address: "",
    email: "",
    origin: "",
    tradeNameError: "",
    brandError: "",
    addressError: "",
    emailError: "",
    originError: "",
  },
]);

const importers = ref([
  {
    tradeName: "",
    brand: "",
    address: "",
    email: "",
    origin: "",
    tradeNameError: "",
    brandError: "",
    addressError: "",
    emailError: "",
    originError: "",
  },
]);

const manufacturerOriginRefs = ref({});
const importerOriginRefs = ref({});
const isSaved = ref(false);
const isSaving = ref(false);
const showSuccessState = ref(false);
const isDirty = ref(false);
const isHydrating = ref(false);
const skipNextSafetyInfoDataReset = ref(false);

const combinedIsSaving = computed(() => {
  return isSaving.value || (props.isSavingAllChanges && isDirty.value);
});

const combinedIsSaved = computed(() => {
  return !isDirty.value || showSuccessState.value || hasValidationError.value;
});

const setManufacturerOriginRef = (el, index) => {
  if (el) {
    manufacturerOriginRefs.value[index] = el;
  }
};

const setImporterOriginRef = (el, index) => {
  if (el) {
    importerOriginRefs.value[index] = el;
  }
};

const getManufacturerData = async () => {
  try {
    const response = await store.dispatch("products/getManufacturerOriginInfo");
    manufacturerOrigins.value = response.data.map((item) => ({
      id: item.value,
      name: `${item.code} - ${item.label}`,
    }));
    } catch (error) {
    return error;
  }
}

const handleOriginChange = (value, index, type) => {
  const selectedId =
    typeof value === "object" && value !== null
      ? value.id ?? value.value ?? ""
      : value;

  if (type === "manufacturer") {
    manufacturers.value[index].origin = selectedId;
    manufacturers.value[index].originError = "";
  } else {
    importers.value[index].origin = selectedId;
    importers.value[index].originError = "";
  }
};

// Dropdown referansını index ve type'a göre döndürür
const getDropdownRef = (index, type) => {
  return type === "manufacturer"
    ? manufacturerOriginRefs.value[index]
    : importerOriginRefs.value[index];
};

// Seçilen id'yi index ve type'a göre döndürür
const getOriginId = (index, type) => {
  return type === "manufacturer"
    ? manufacturers.value[index]?.origin
    : importers.value[index]?.origin;
};

// id'ye göre manufacturerOrigins listesinden eşleşen item'ı bulur
const findSelectedOriginItem = (originId) => {
  return manufacturerOrigins.value.find(
    (origin) => String(origin.id || origin.value || "") === String(originId)
  );
};

// Radio button listesinden seçili olan element'i bulur
const getSelectedRadioElement = (listElement) => {
  const checkedInput = listElement.querySelector('input[type="radio"][checked]');
  const selectedElement = checkedInput?.closest('.radioButton-container')
    || listElement.querySelector('.radioButton-container .radioButton.selected')?.closest('.radioButton-container');

  return selectedElement;
};

// Seçili element'i görünür alana scroll eder
const scrollToSelectedElement = (selectedElement) => {
  if (selectedElement) {
    selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
};

// Dropdown açıldığında seçili origin'i bulup radio button'ı seçer ve scroll eder
const handleDropdownStateChange = async (isOpen, index, type) => {
  if (!isOpen) return;

  await nextTick();

  const dropdownRef = getDropdownRef(index, type);
  if (!dropdownRef) return;

  const originId = getOriginId(index, type);
  if (!originId) return;

  const radioButtonGroupRef = dropdownRef.$refs?.radioButtonGroup;
  if (!radioButtonGroupRef) return;

  const selectedItem = findSelectedOriginItem(originId);
  if (!selectedItem) return;

  radioButtonGroupRef.setSelectedItem(selectedItem);

  await nextTick();
  const listElement = radioButtonGroupRef.$el;
  if (!listElement) return;

  const selectedElement = getSelectedRadioElement(listElement);
  scrollToSelectedElement(selectedElement);
};

const getManufacturerOriginInfo = async () => {
  if (!props.safetyInfoData) return;

  try {
    isHydrating.value = true;
    const safetyResponse = { data: props.safetyInfoData };

    if (safetyResponse?.data?.providers && Array.isArray(safetyResponse.data.providers)) {
      const providerManufacturers = safetyResponse.data.providers
        .filter((provider) => provider.type === "MANUFACTURER")
        .map((provider) => ({
          tradeName: provider.name || "",
          brand: provider.brand || "",
          address: provider.address || "",
          email: provider.email || "",
          origin: provider.origin?.value ? String(provider.origin.value) : "",
          originLabel: provider.origin?.label || "",
          tradeNameError: "",
          brandError: "",
          addressError: "",
          emailError: "",
          originError: "",
        }));

      const providerImporters = safetyResponse.data.providers
        .filter((provider) => provider.type === "IMPORTER")
        .map((provider) => ({
          tradeName: provider.name || "",
          brand: provider.brand || "",
          address: provider.address || "",
          email: provider.email || "",
          origin: provider.origin?.value ? String(provider.origin.value) : "",
          originLabel: provider.origin?.label || "",
          tradeNameError: "",
          brandError: "",
          addressError: "",
          emailError: "",
          originError: "",
        }));

      const existingOriginsMap = new Map();
      manufacturerOrigins.value.forEach((origin) => {
        const originId = String(origin.id || origin.value || "");
        existingOriginsMap.set(originId, origin);
      });

      safetyResponse.data.providers
        .filter((provider) =>
          provider.type === "MANUFACTURER" || provider.type === "IMPORTER"
        )
        .forEach((provider) => {
          if (provider.origin && provider.origin.value) {
            const originId = String(provider.origin.value);
            if (!existingOriginsMap.has(originId)) {
              existingOriginsMap.set(originId, {
                id: originId,
                name: `${provider.origin.code || ""} - ${provider.origin.label || ""}`.trim(),
              });
            }
          }
        });

      manufacturerOrigins.value = Array.from(existingOriginsMap.values());

      if (providerManufacturers.length > 0) {
        manufacturers.value = providerManufacturers;
      }

      if (providerImporters.length > 0) {
        importers.value = providerImporters;
      }

      await nextTick();
      if (manufacturerOrigins.value.length > 0) {
        manufacturers.value.forEach((manufacturer, index) => {
          if (manufacturer.origin) {
            const selectedOrigin = manufacturerOrigins.value.find(
              (origin) => {
                if (!origin) return false;
                const originId = String(origin.id || origin.value || "");
                const manufacturerOrigin = String(manufacturer.origin || "");
                return originId === manufacturerOrigin;
              }
            );
            if (selectedOrigin) {
              handleOriginChange(selectedOrigin, index, "manufacturer");
              const dropdownRef = manufacturerOriginRefs.value[index];
              if (dropdownRef) {
                dropdownRef.selectedRadioItem = selectedOrigin;
              }
            }
          }
        });

        importers.value.forEach((importer, index) => {
          if (importer.origin) {
            const selectedOrigin = manufacturerOrigins.value.find(
              (origin) => {
                if (!origin) return false;
                const originId = String(origin.id || origin.value || "");
                const importerOrigin = String(importer.origin || "");
                return originId === importerOrigin;
              }
            );
            if (selectedOrigin) {
              handleOriginChange(selectedOrigin, index, "importer");
              const dropdownRef = importerOriginRefs.value[index];
              if (dropdownRef) {
                dropdownRef.selectedRadioItem = selectedOrigin;
              }
            }
          }
        });
      }
    }

    queueMicrotask(() => {
      isHydrating.value = false;
    });
  } catch (error) {
    isHydrating.value = false;
    return error;
  }
};

const hasAnyFieldFilled = (e) =>
  !!(e.tradeName || e.address || e.email || e.origin);

const checkManufacturerError = (index) => {
  const manufacturer = manufacturers.value[index];

  if (!hasAnyFieldFilled(manufacturer)) {
    manufacturer.tradeNameError = "";
    manufacturer.addressError = "";
    manufacturer.emailError = "";
    manufacturer.originError = "";
    manufacturer.brandError = "";
    return;
  }

  if (!manufacturer.tradeName) {
    manufacturer.tradeNameError = "Kayıtlı ticari ünvan girmelisiniz.";
  } else {
    manufacturer.tradeNameError = "";
  }

  manufacturer.brandError = "";

  if (!manufacturer.address) {
    manufacturer.addressError = "Adres girmelisiniz.";
  } else {
    manufacturer.addressError = "";
  }

  if (!manufacturer.email) {
    manufacturer.emailError = "E-posta girmelisiniz.";
  } else if (!isValidEmail(manufacturer.email)) {
    manufacturer.emailError =
      "Lütfen e-posta bilgisini formata uygun yazın. (Örn: isim@örnek.com)";
  } else {
    manufacturer.emailError = "";
  }

  if (!manufacturer.origin || manufacturer.origin === "") {
    manufacturer.originError = "Menşei seçmelisiniz.";
  } else {
    manufacturer.originError = "";
  }
};

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const checkImporterError = (index) => {
  const importer = importers.value[index];

  if (!hasAnyFieldFilled(importer)) {
    importer.tradeNameError = "";
    importer.addressError = "";
    importer.emailError = "";
    importer.originError = "";
    importer.brandError = "";
    return;
  }

  if (!importer.tradeName) {
    importer.tradeNameError = "Kayıtlı ticari ünvan girmelisiniz.";
  } else {
    importer.tradeNameError = "";
  }

  importer.brandError = "";

  if (!importer.address) {
    importer.addressError = "İletişim kurulabilecek adres girmelisiniz.";
  } else {
    importer.addressError = "";
  }

  if (!importer.email) {
    importer.emailError = "E-posta girmelisiniz.";
  } else if (!isValidEmail(importer.email)) {
    importer.emailError =
      "Lütfen e-posta bilgisini formata uygun yazın. (Örn: isim@örnek.com)";
  } else {
    importer.emailError = "";
  }

  if (!importer.origin || importer.origin === "") {
    importer.originError = "Menşei seçmelisiniz.";
  } else {
    importer.originError = "";
  }
};

watch(
  manufacturers,
  (list) => {
    list.forEach((_, idx) => checkManufacturerError(idx));
  },
  { deep: true }
);

watch(
  importers,
  (list) => {
    list.forEach((_, idx) => checkImporterError(idx));
  },
  { deep: true }
);

watch(
  [manufacturers, importers],
  () => {
    if (isHydrating.value) return;
    isDirty.value = true;
    showSuccessState.value = false;
    emit('hasChanges');
  },
  { deep: true }
);

const getManufacturerInfo = () => {
  let allValid = true;

  manufacturers.value.forEach((_, index) => {
    checkManufacturerError(index);
    const m = manufacturers.value[index];
    const hasErrors =
      m.tradeNameError || m.addressError || m.emailError || m.originError;
    if (hasErrors) allValid = false;
  });

  importers.value.forEach((_, index) => {
    checkImporterError(index);
    const i = importers.value[index];
    const hasErrors =
      i.tradeNameError || i.addressError || i.emailError || i.originError;
    if (hasErrors) allValid = false;
  });

  const businessEntities = [];
  manufacturers.value.forEach((m) => {
    if (m.tradeName || m.brand || m.address || m.email || m.origin) {
      businessEntities.push({
        type: "MANUFACTURER",
        name: m.tradeName,
        brand: m.brand,
        address: m.address,
        email: m.email,
        originId:
          typeof m.origin === "object" && m.origin !== null
            ? m.origin.id ?? m.origin.value ?? null
            : m.origin,
      });
    }
  });
  importers.value.forEach((i) => {
    if (i.tradeName || i.brand || i.address || i.email || i.origin) {
      businessEntities.push({
        type: "IMPORTER",
        name: i.tradeName,
        brand: i.brand,
        address: i.address,
        email: i.email,
        originId:
          typeof i.origin === "object" && i.origin !== null
            ? i.origin.id ?? i.origin.value ?? null
            : i.origin,
      });
    }
  });

  return {
    data: businessEntities,
    isValid: allValid,
  };
};

const addManufacturer = () => {
  if (manufacturers.value.length < 5) {
    manufacturers.value.push({
      tradeName: "",
      brand: "",
      address: "",
      email: "",
      origin: "",
      tradeNameError: "",
      brandError: "",
      addressError: "",
      emailError: "",
      originError: "",
    });
  }
};

const addImporter = () => {
  if (importers.value.length < 5) {
    importers.value.push({
      tradeName: "",
      brand: "",
      address: "",
      email: "",
      origin: "",
      tradeNameError: "",
      brandError: "",
      addressError: "",
      emailError: "",
      originError: "",
    });
  }
};

const removeManufacturer = (index) => {
  if (manufacturers.value.length > 1) {
    manufacturers.value.splice(index, 1);
  }
};

const removeImporter = (index) => {
  if (importers.value.length > 1) {
    importers.value.splice(index, 1);
  }
};

watch(
  () => props.safetyInfoData,
  async (newData) => {
    if (!newData) return;
    await getManufacturerOriginInfo();

    if (skipNextSafetyInfoDataReset.value) {
      skipNextSafetyInfoDataReset.value = false;
      return;
    }
    showSuccessState.value = false;
  },
  { immediate: true }
);


onMounted(async () => {
  await getManufacturerData();
});

const handleSaveChanges = async () => {
  if (!props.onSaveChanges) return;

  try {
    isSaving.value = true;

    await props.onSaveChanges();

    const productDetail = store.getters['products/getProductDetail'];
    sendProductDetailEvent(GA4_EVENT.SAVE_CHANGES, {
      button_name: 'Değişiklikleri Kaydet',
      button_type: 'Save Changes',
      button_area: 'Safety Seller & Manufacturer Information',
      item_id: productDetail?.id,
      all_variants_checkbox: props.applyToVariants,
    });

    showSuccessState.value = true;
    isDirty.value = false;
    store.commit(
      'common/SET_TOASTER_DATA',
      {
        type: 'success',
        title: 'Başarılı',
        description: 'Yaptığınız değişiklikler kaydedilmiştir.',
      },
    );
  } catch (error) {
    const errorMessage = error?.data?.errors?.[0]?.message || 'Üretici Bilgileri güncellenirken bir hata oluştu.';
    store.commit(
      'common/SET_TOASTER_DATA',
      {
        type: 'error',
        title: 'Hata',
        description: errorMessage,
      }
    );
    showSuccessState.value = false;
    throw error;
  } finally {
    isSaving.value = false;
  }
};

const showSaveAllSuccessState = () => {
  showSuccessState.value = true;
  isDirty.value = false;
  skipNextSafetyInfoDataReset.value = true;
};

defineExpose({
  combinedIsSaved,
  getManufacturerInfo,
  showSaveAllSuccessState,
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.manufacturer-business-area {
  display: flex;
  justify-content: space-around;
  margin-bottom: 16px;

  &-button {
    display: flex;
    gap: 8px;
    color: $colorM50;
    cursor: pointer;

    p {
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05px;
    }

    &.disabled {
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: none;
      img {
        filter: grayscale(100%);
        opacity: 0.5;
      }
      p {
        color: $colorN50;
      }
    }
  }
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.textarea-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.box {
  flex: 1;

  &:last-child {
    margin-left: 25px;
  }
  .box-form-area {
    textarea {
      color: $colorN80;
      border: 1px solid $colorD30;

      &::placeholder {
        font-size: 12px;
        line-height: 18px;
        color: #4e5c78;
      }
    }
  }
  .title-area {
    .title {
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05px;
      margin-bottom: 8px;
    }

    .description {
      font-size: 11px;
      line-height: 14px;
      height: 30px;
    }
  }
  &-form-area {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 16px 0px;

    &.disabled {
      .input-wrapper,
      .textarea-wrapper {
        pointer-events: none;

        :deep(input),
        :deep(textarea),
        :deep(.input-wrapper),
        :deep(.input-container) {
          pointer-events: none;
        }
      }

      :deep(.dropdownSelectable) {
        pointer-events: none;
        
        .dropdownSelectable-btn {
          background-color: #f6f7f9;
        }
      }
    }
  }
}

.limit-text,
.error-text {
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
  color: $colorE50;
  margin-left: 12px;
}
.remove-button {
  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;

    .remove-button-content {
      opacity: 0.6;

      :deep(svg),
      :deep(path) {
        opacity: 0.6;
      }

      span {
        color: $colorN50;
      }
    }
  }
}

.remove-button-content {
  display: flex;
  justify-content: right;
  gap: 4px;
  color: $colorM50;

  img {
    width: 16px;
    height: 16px;
  }

  .span {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: $colorM50;
    align-self: flex-end;
  }
}
</style>
